import { useEffect } from 'react';
import i18n from '../i18n';

/**
 * Lightweight SEO hook: sets <title>, meta description, canonical URL and hreflang alternates.
 * Avoids adding extra deps (react-helmet) — direct DOM manipulation in effects.
 */
const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    Object.keys(attrs).forEach((k) => {
      if (k !== 'content') el.setAttribute(k, attrs[k]);
    });
    document.head.appendChild(el);
  }
  if (attrs.content !== undefined) el.setAttribute('content', attrs.content);
};

const upsertLink = (rel, href, attrs = {}) => {
  const selectorParts = [`link[rel="${rel}"]`];
  if (attrs.hreflang) selectorParts.push(`[hreflang="${attrs.hreflang}"]`);
  let el = document.head.querySelector(selectorParts.join(''));
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    Object.keys(attrs).forEach((k) => el.setAttribute(k, attrs[k]));
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const buildHrefWithLang = (currentUrl, lang) => {
  try {
    const u = new URL(currentUrl);
    if (lang === 'x-default') {
      u.searchParams.delete('lang');
    } else {
      u.searchParams.set('lang', lang);
    }
    return u.toString();
  } catch { return currentUrl; }
};

export const useDocumentMeta = ({ title, description, canonical, image, type = 'website' }) => {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      upsertMeta('meta[name="description"]', { name: 'description', content: description });
      upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
      upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    }
    if (title) {
      upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title });
      upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    }
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: type });
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    // og:locale
    const lng = i18n.language === 'ar' ? 'ar_DZ' : 'fr_FR';
    upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: lng });
    if (image) {
      upsertMeta('meta[property="og:image"]', { property: 'og:image', content: image });
      upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: image });
    }
    const url = canonical || (typeof window !== 'undefined' ? window.location.href : '');
    if (url) {
      upsertLink('canonical', url);
      upsertMeta('meta[property="og:url"]', { property: 'og:url', content: url });
      // hreflang alternates for FR/AR/x-default
      upsertLink('alternate', buildHrefWithLang(url, 'fr'), { hreflang: 'fr' });
      upsertLink('alternate', buildHrefWithLang(url, 'ar'), { hreflang: 'ar' });
      upsertLink('alternate', buildHrefWithLang(url, 'x-default'), { hreflang: 'x-default' });
    }
  }, [title, description, canonical, image, type]);
};

export default useDocumentMeta;
